import { colors } from "@quickpoint.me/shared";
import styled, { keyframes, css } from "styled-components";
import type { ThemeProps } from "./types";

// Keyframes
export const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Focus styles mixin
export const focusStyles = (props: ThemeProps) => css`
	&:focus {
		outline: 3px solid ${props.$useNdrColors ? props.$text : colors.brand.purple};
		outline-offset: 2px;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}
`;

// Main container
export const SettingsContainer = styled.div<ThemeProps & { $sliderHasMoved: boolean }>`
	position: absolute;
	z-index: 102;
	font-size: 1.2rem;
	top: 0;
	left: 0;
	background: ${(props) => {
		if (props.$useNdrColors) {
			return props.$sliderHasMoved
				? `${props.$background}F2`
				: props.$background;
		}
		return props.$sliderHasMoved ? "rgba(255, 255, 255, 0.98)" : "#fff";
	}};
	color: ${(props) => (props.$useNdrColors ? props.$text : "#000")};
	height: 100svh;
	width: 100vw;
	font-family: Arial, sans-serif;
	overflow: hidden;
	display: flex;
	flex-direction: column;

	.react-toggle {
		margin: 0;
	}
	.react-toggle--checked .react-toggle-track {
		background-color: ${colors.brand.purple};
	}
`;

export const SettingsWrapper = styled.div`
	flex: 1;
	overflow-y: auto;
	min-height: 0;
	display: flex;
	justify-content: center;
`;

export const SettingsContent = styled.div`
	width: 100%;
	max-width: 600px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	box-sizing: border-box;
`;

export const SettingsTitle = styled.h2`
	font-size: 2rem;
	margin-bottom: 0.5rem;
	text-align: center;
`;

// Accordion components
export const Accordion = styled.details<ThemeProps>`
	border: 2px solid ${(props) => (props.$useNdrColors ? props.$text : "#E5E1F8")};
	border-radius: 16px;
	background-color: ${(props) => (props.$useNdrColors ? props.$background : "rgba(255,255,255,0.5)")};

	&[open] summary {
		border-bottom: 2px solid ${(props) => (props.$useNdrColors ? props.$text : "#E5E1F8")};
	}

	&[open] summary::after {
		content: "−";
	}
`;

export const AccordionSummary = styled.summary<ThemeProps>`
	padding: 20px;
	font-size: 1.3rem;
	font-weight: bold;
	cursor: pointer;
	list-style: none;
	display: flex;
	align-items: center;
	justify-content: space-between;
	user-select: none;

	&::-webkit-details-marker {
		display: none;
	}

	&::after {
		content: "+";
		font-size: 1.5rem;
		font-weight: bold;
		transition: transform 0.2s ease;
	}

	&:hover {
		background-color: ${(props) => (props.$useNdrColors ? `${props.$text}11` : "#E5E1F822")};
	}

	&:focus {
		outline: 3px solid ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
		outline-offset: -3px;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}
`;

export const AccordionContent = styled.div`
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

// Control components
export const ControlRow = styled.div<{ $column?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;

	label {
		flex: 1;
		font-weight: bold;
		cursor: pointer;
	}

	${(props) =>
		props.$column &&
		css`
			flex-direction: column;
			align-items: flex-start;
		`}
`;

export const ControlRowInner = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	align-items: center;
`;

export const SubSection = styled.div<ThemeProps>`
	border-top: 1px solid ${(props) => (props.$useNdrColors ? `${props.$text}44` : "#ccc")};
	padding-top: 16px;
	margin-top: 8px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

export const SubSectionTitle = styled.div`
	font-size: 1.1rem;
	font-weight: bold;
	margin-bottom: 8px;
`;

// Form elements
export const StyledSelect = styled.select<ThemeProps>`
	width: 100%;
	padding: 12px;
	font-size: 1rem;
	border-radius: 8px;
	border: 2px solid ${(props) => (props.$useNdrColors ? props.$text : "#E5E1F8")};
	background-color: ${(props) => (props.$useNdrColors ? props.$background : "#fff")};
	color: inherit;
`;

export const StyledRangeInput = styled.input<ThemeProps>`
	width: 100%;
	accent-color: ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
	height: 24px;
`;

export const ColorPickerContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	width: 100%;

	.react-colorful {
		width: 100%;
		height: 200px;
	}
`;

// Text styles
export const WarningText = styled.p`
	font-size: 0.85rem;
	color: #d32f2f;
	margin-top: 4px;
	font-style: italic;
`;

export const HintText = styled.p`
	font-size: 0.9rem;
	font-style: italic;
	opacity: 0.8;
`;

// Spinner
export const Spinner = styled.span<ThemeProps>`
	width: 18px;
	height: 18px;
	border: 3px solid transparent;
	border-top-color: ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
	border-right-color: ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
	border-radius: 50%;
	animation: ${spin} 0.8s linear infinite;
`;

// Export PDF Button
export const ExportPdfButton = styled.button<ThemeProps>`
	width: 100%;
	padding: 14px 24px;
	font-size: 1.1rem;
	border-radius: 12px;
	border: 2px solid ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
	background: transparent;
	color: ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
	cursor: pointer;
	font-weight: bold;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;

	&:hover:not(:disabled) {
		background: ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
		color: ${(props) => (props.$useNdrColors ? props.$background : "#fff")};

		${Spinner} {
			border-top-color: ${(props) => (props.$useNdrColors ? props.$background : "#fff")};
			border-right-color: ${(props) => (props.$useNdrColors ? props.$background : "#fff")};
		}
	}

	${(props) => focusStyles(props)}

	&:disabled {
		opacity: 0.7;
		cursor: wait;
	}
`;

// Footer components
export const SettingsFooter = styled.div<ThemeProps>`
	flex-shrink: 0;
	display: flex;
	justify-content: center;
	padding: 16px 20px;
	background: ${(props) => (props.$useNdrColors ? props.$background : "#fff")};
	border-top: 2px solid ${(props) => (props.$useNdrColors ? props.$text : "#E5E1F8")};
	box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
`;

export const FooterContent = styled.div`
	width: 100%;
	max-width: 600px;
	display: flex;
	justify-content: center;
`;

export const DoneButton = styled.button<ThemeProps>`
	padding: 14px 48px;
	font-size: 1.2rem;
	border-radius: 30px;
	border: 2px solid transparent;
	background: ${(props) => (props.$useNdrColors ? props.$text : colors.brand.purple)};
	color: ${(props) => (props.$useNdrColors ? props.$background : "#fff")};
	cursor: pointer;
	font-weight: bold;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		opacity: 0.9;
	}

	${(props) => focusStyles(props)}
`;

// Source code viewer components
export const SourceCodeContainer = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100svh;
	background: white;
	z-index: 105;
	display: flex;
	flex-direction: column;
`;

export const SourceCodeHeader = styled.div`
	padding: 16px;
	display: flex;
	justify-content: space-between;
	background: #eee;
	border-bottom: 1px solid #ccc;
	z-index: 106;
`;

export const SourceCodeButton = styled.button`
	padding: 8px 16px;
	font-size: 1rem;
	cursor: pointer;
`;
